import { action, makeObservable, observable, reaction } from 'mobx'

import { IRemoteTileset } from '~/client/graph'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'
import { toast } from '~/client/src/shared/utils/toast'

import {
  ACCEPTABLE_WMTS_TILE_STATUSES,
  detectOGCServiceType,
  getOGCCapabilities,
  standardizeWMTSTileUrl,
  validateWMTSLink,
  WMTSCapabilities,
  WMTSValidationResult,
} from './wmtsCapabilities'

const fillRequiredFields = 'Please fill in the required fields'
const wmtsValidationWarning =
  'Finalized Url validation failed, but with acceptable statuses (such as empty or protected tiles). You can still try to add it, but we do not guarantee that it will work.'
const wmtsValidationError =
  'Finalized Url validation failed. Please check the provided parameters.'
const urlCanNotBeValidated =
  'The provided URL cannot be validated. You can still try to add it, but we do not guarantee that it will work.'

interface WMTSMandatorySearchParams {
  SERVICE: 'WMTS'
  REQUEST: string
  VERSION: string
  LAYER: string
  STYLE: string
  TILEMATRIXSET: string
  TILEMATRIX: string
  TILEROW: string
  TILECOL: string
  FORMAT: string
  [key: string]: string // Allow for dynamic dimension parameters
}

export class UploadRemoteTilesetDialogStore {
  @observable
  public wmtsCapabilities: WMTSCapabilities = null
  @observable
  public detectedServiceType: 'WMTS' | 'Unknown' | null = null
  @observable
  public remoteTileset: IRemoteTileset = {
    source: '',
    name: '',
    projectId: '',
  }
  @observable
  public selectedLayerIndex: number = 0
  @observable
  public selectedFormat: string = ''
  @observable
  public selectedTileMatrixSet: string = ''
  @observable
  public selectedResourceUrl: string = ''
  @observable
  public selectedStyle: string = ''
  @observable
  public selectedDimensions: { [dimensionId: string]: string } = {}
  @observable
  public isLoadingCapabilities: boolean = false
  @observable
  public isValidatingResLink: boolean = false
  @observable
  public validationResultWMTS: WMTSValidationResult = null

  public get isSaveButtonActive(): boolean {
    return (
      !this.hasSomeError &&
      !this.isLoadingCapabilities &&
      !this.isValidatingResLink
    )
  }

  public constructor(private readonly eventsStore: EventsStore) {
    makeObservable(this)

    reaction(() => this.resLink, this.validateResLinkReaction)
  }

  private validateResLinkReaction = async (resLink: string) => {
    if (resLink && this.wmtsCapabilities) {
      this.isValidatingResLink = true
      this.validationResultWMTS = null
      this.validationResultWMTS = await validateWMTSLink(
        resLink,
        this.wmtsCapabilities,
        this.selectedLayerIndex,
        this.selectedTileMatrixSet,
      )
      this.isValidatingResLink = false
    } else {
      this.validationResultWMTS = null
    }
  }

  public get remoteTilesetInput(): IRemoteTileset {
    return {
      source: this.resLink,
      name: this.remoteTileset.name,
      projectId: this.eventsStore.appState.activeProject?.id || '',
    }
  }

  public get isWMTSValidationHasWarnings(): boolean {
    return (
      this.validationResultWMTS &&
      !this.validationResultWMTS.isValid &&
      ACCEPTABLE_WMTS_TILE_STATUSES.includes(this.validationResultWMTS.status)
    )
  }

  public get isWMTSValidationHasErrors(): boolean {
    return (
      this.validationResultWMTS &&
      !this.validationResultWMTS.isValid &&
      !ACCEPTABLE_WMTS_TILE_STATUSES.includes(this.validationResultWMTS.status)
    )
  }

  public get hasSomeError() {
    return !this.isRequiredFieldsFilled || this.isWMTSValidationHasErrors
  }

  public get hasSomeWarning() {
    return this.isWMTSValidationHasWarnings || this.isResUrlCanNotBeValidated
  }

  public get errorText(): string {
    if (!this.isRequiredFieldsFilled) {
      return fillRequiredFields
    }
    if (this.isWMTSValidationHasErrors) {
      return wmtsValidationError
    }
  }

  public get warningText(): string {
    if (this.isWMTSValidationHasWarnings) {
      return wmtsValidationWarning
    }
    if (this.isResUrlCanNotBeValidated) {
      return urlCanNotBeValidated
    }
  }

  public get isResUrlCanNotBeValidated(): boolean {
    return (
      this.isRequiredFieldsFilled &&
      !this.wmtsCapabilities &&
      !this.isLoadingCapabilities
    )
  }

  public get isRequiredFieldsFilled(): boolean {
    return (
      this.remoteTileset.name?.length > 1 &&
      this.remoteTileset.source?.length > 1
    )
  }

  @action.bound
  public cleanup() {
    this.remoteTileset = {
      source: '',
      name: '',
      projectId: '',
    }
    this.wmtsCapabilities = null
    this.detectedServiceType = null
    this.selectedLayerIndex = 0
    this.selectedFormat = ''
    this.selectedTileMatrixSet = ''
    this.selectedResourceUrl = ''
    this.selectedStyle = ''
    this.selectedDimensions = {}
  }

  @action.bound
  public updateName(event: React.ChangeEvent<HTMLInputElement>) {
    this.remoteTileset.name = event.target.value
  }

  @action.bound
  public handleFormatChange(event: React.ChangeEvent<HTMLSelectElement>) {
    this.selectedFormat = event.target.value
  }

  @action.bound
  public handleTileMatrixSetChange(
    event: React.ChangeEvent<HTMLSelectElement>,
  ) {
    this.selectedTileMatrixSet = event.target.value
  }

  @action.bound
  public handleResourceUrlChange(event: React.ChangeEvent<HTMLSelectElement>) {
    this.selectedResourceUrl = event.target.value

    // Clear dimension selections when ResourceURL changes since
    // different templates may require different dimensions
    this.selectedDimensions = {}
  }

  @action.bound
  public handleStyleChange(event: React.ChangeEvent<HTMLSelectElement>) {
    this.selectedStyle = event.target.value
  }

  @action.bound
  public handleDimensionChange(dimensionId: string, value: string) {
    this.selectedDimensions = {
      ...this.selectedDimensions,
      [dimensionId]: value,
    }
  }

  @action.bound
  public handleLayerChange(event: React.ChangeEvent<HTMLSelectElement>) {
    this.selectedLayerIndex = Number(event.target.value)
    // Reset dependent selections when layer changes
    this.remoteTileset.name =
      this.wmtsCapabilities?.layers?.[this.selectedLayerIndex]?.title?.trim() ||
      this.remoteTileset.name
    this.selectedFormat = ''
    this.selectedTileMatrixSet = ''
    this.selectedResourceUrl = ''
    this.selectedStyle = ''
    this.selectedDimensions = {}
  }

  @action.bound
  public async updateUrl(event: React.ChangeEvent<HTMLInputElement>) {
    this.wmtsCapabilities = null
    this.detectedServiceType = null
    this.selectedLayerIndex = 0
    this.selectedFormat = ''
    this.selectedTileMatrixSet = ''
    this.selectedResourceUrl = ''
    this.selectedStyle = ''
    this.selectedDimensions = {}
    let decodedLink: string
    try {
      decodedLink = decodeURIComponent(event.target.value)
    } catch (error) {
      console.error('Failed to decode URI component:', error)
      decodedLink = event.target.value
    }
    this.remoteTileset.source = decodedLink

    if (decodedLink.length > 1) {
      try {
        this.isLoadingCapabilities = true
        // First detect the service type
        const serviceType = await detectOGCServiceType(decodedLink)

        if (serviceType !== 'WMTS') {
          this.detectedServiceType = 'Unknown'
          this.wmtsCapabilities = null
          return
        }

        this.detectedServiceType = 'WMTS'
        // Get WMTS capabilities
        const capabilities = await getOGCCapabilities(decodedLink)
        if (capabilities?.type === 'WMTS') {
          this.wmtsCapabilities = capabilities
          this.remoteTileset.name =
            this.remoteTileset.name ||
            this.wmtsCapabilities.layers[0]?.title ||
            ''
        }
      } catch (error) {
        console.error('Error fetching WMTS capabilities:', error)
        this.wmtsCapabilities = null
        this.detectedServiceType = 'Unknown'
      } finally {
        this.isLoadingCapabilities = false
      }
    }
  }

  public get resLink(): string {
    if (!this.remoteTileset.source || !this.wmtsCapabilities) {
      return standardizeWMTSTileUrl(this.remoteTileset.source)
    }

    try {
      const baseUrl = new URL(this.remoteTileset.source)

      // Clear existing service-related params
      const paramsToRemove = [
        'service',
        'request',
        'version',
        'layer',
        'style',
        'format',
        'tilematrixset',
        'tilematrix',
        'tilerow',
        'tilecol',
        'time',
        'elevation',
        'band', // Add common dimension params
      ]
      paramsToRemove.forEach(param => {
        baseUrl.searchParams.delete(param.toLowerCase())
        baseUrl.searchParams.delete(param.toUpperCase())
      })

      const selectedLayer =
        this.wmtsCapabilities.layers[this.selectedLayerIndex]

      // Determine style to use
      let styleToUse = 'default'
      if (this.selectedStyle) {
        styleToUse = this.selectedStyle
      } else if (selectedLayer?.styles?.length > 0) {
        const defaultStyle = selectedLayer.styles.find(s => s.isDefault)
        styleToUse =
          defaultStyle?.identifier ||
          selectedLayer.styles[0]?.identifier ||
          'default'
      }

      // Determine tile matrix set to use
      const tileMatrixSetToUse =
        this.selectedTileMatrixSet ||
        selectedLayer?.tileMatrixSetLinks?.[0] ||
        'WEBMERCATOR'

      // Get dimension values
      const dimensionValues = { ...this.selectedDimensions }

      // Add default values for dimensions that aren't selected
      selectedLayer?.dimensions?.forEach(dimension => {
        if (!dimensionValues[dimension.identifier] && dimension.default) {
          dimensionValues[dimension.identifier] = dimension.default
        }
      })

      // If a specific ResourceURL is selected, use it with parameter substitution
      if (this.selectedResourceUrl && selectedLayer?.resourceUrls?.length > 0) {
        const selectedResourceUrlObj = selectedLayer.resourceUrls.find(
          ru => ru.template === this.selectedResourceUrl,
        )
        if (selectedResourceUrlObj) {
          return standardizeWMTSTileUrl(
            selectedResourceUrlObj.template,
            styleToUse,
            tileMatrixSetToUse,
            this.selectedFormat || selectedLayer.formats?.[0] || 'image/png',
            dimensionValues,
          )
        }
      }

      // If layer has ResourceURL templates and none specifically selected, use the first one
      if (
        selectedLayer &&
        selectedLayer.resourceUrls?.length > 0 &&
        !this.selectedResourceUrl
      ) {
        return standardizeWMTSTileUrl(
          selectedLayer.resourceUrls[0].template,
          styleToUse,
          tileMatrixSetToUse,
          this.selectedFormat || selectedLayer.formats?.[0] || 'image/png',
          dimensionValues,
        )
      }

      // Otherwise build KVP-style URL
      if (selectedLayer) {
        const wmtsParams: WMTSMandatorySearchParams = {
          SERVICE: 'WMTS',
          REQUEST: 'GetTile',
          VERSION: '1.0.0',
          LAYER: selectedLayer.identifier,
          STYLE: styleToUse,
          TILEMATRIXSET: tileMatrixSetToUse,
          TILEMATRIX: '{z}',
          TILEROW: '{y}',
          TILECOL: '{x}',
          FORMAT:
            this.selectedFormat || selectedLayer.formats?.[0] || 'image/png',
        }

        // Add dimension parameters
        Object.entries(dimensionValues).forEach(([key, value]) => {
          if (value) {
            wmtsParams[key.toUpperCase()] = value
          }
        })

        Object.entries(wmtsParams).forEach(([key, value]) => {
          baseUrl.searchParams.set(key, value as string)
        })
      }

      return baseUrl.toString()
    } catch (error) {
      console.error('Error building resLink:', error)
      return this.remoteTileset.source
    }
  }

  public copyText = async (): Promise<void> => {
    await navigator.clipboard
      .writeText(this.resLink)
      .then(() => toast.success(Localization.translator.copiedToClipboard))
  }
}
